import { useAudio } from '@providers'
import classNames from 'classnames'
import { motion } from 'framer-motion'
import { memo, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { useIsMobile } from '@ds'
import {
  InventoryCard,
  useGetBoosters,
  useGetCardsSkins,
  useGetCardsQuest,
  useInventory,
  useInventoryActions,
} from '@store'

import { InventoryButton } from '../button'
import { InventoryGrid } from '../grid'
import { InventoryPager } from '../pager'
import { InventoryRow } from '../row'
import { InventoryTabs } from '../tabs'
import styles from './Inventory.module.scss'
import {
  variantFullscreenTabs,
  variantFullscreenTitle,
  variantGrid,
  variantInner,
  variantMinimizedTabs,
  variantRow,
} from './helpers'
import { useCard } from './hooks/useCard'
import { ModalContent } from './modalContent/ModalContent'

export const Inventory = memo(function Inventory() {
  const { t } = useTranslation()
  const activeTab = useInventory.store.activeTab()
  const mode = useInventory.store.mode()
  const isOpen = useInventory.store.isOpen()
  const { setActiveTab, toggle, setMode, reserveCard } = useInventoryActions()
  const isMobile = useIsMobile()
  const cardsSkins = useGetCardsSkins()
  const cardsQuest = useGetCardsQuest()
  const cardsitems = useGetBoosters()
  const { playClick, playSwitchOn } = useAudio()

  const location = useLocation()
  const { questId } = useParams()
  const [activeCard, setActiveCard] = useState<InventoryCard | null>(null)
  const { useCardHandler } = useCard()

  const isInsideQuest = Boolean(
    location.pathname.startsWith('/quest/') && questId
  )
  const isInsightKey = activeCard?.title === 'Insight Key'
  const showUseButton = !isInsightKey || isInsideQuest

  useEffect(() => {
    toggle(false)

    if (location.pathname === '/home') {
      setMode('fullscreen')
    } else {
      setMode('minimized')
    }

    setActiveTab(0)
  }, [location.pathname])

  const handleCardClick = (card: InventoryCard) => {
    if (card.type === 'empty') {
      return
    }

    setActiveCard(card)
  }

  const handleDrop = (card: InventoryCard) => {
    reserveCard(card)
    // clickSound.play()
    // toggle(false)
  }

  const handleCloseModal = useCallback(() => {
    setActiveCard(null)
  }, [])

  const handleUseCard = useCallback(() => {
    playSwitchOn()
    if (activeCard) {
      const questSlug = isInsightKey && isInsideQuest ? questId : undefined;
      useCardHandler(activeCard, questSlug).then(() => {
        setActiveCard(null)
      })
    }
  }, [
    activeCard,
    isInsightKey,
    isInsideQuest,
    questId,
    useCardHandler,
    playSwitchOn,
  ])

  return (
    <div
      className={classNames(
        styles.inventory,
        mode === 'minimized' && styles.isMinimized,
        mode === 'fullscreen' && styles.isFullscreen,
        isMobile && styles.isMobile
      )}
    >
      <InventoryButton />
      {!!activeCard && (
        <ModalContent
          card={activeCard}
          onClick={handleUseCard}
          onClose={handleCloseModal}
          state="use"
          showUseButton={showUseButton}
        />
      )}

      <motion.div
        className={styles.inner}
        variants={variantInner}
        animate={isOpen ? 'show' : 'hide'}
        initial="hide"
      >
        {isOpen ? (
          <>
            {mode === 'fullscreen' ? (
              <motion.div
                className={styles.title}
                variants={variantFullscreenTitle}
                animate={isOpen ? 'show' : 'hide'}
                initial="hide"
              >
                {t('shared.inventory.inventory')}
              </motion.div>
            ) : null}

            <motion.div
              className={styles.tabsWrap}
              variants={
                mode === 'fullscreen'
                  ? variantFullscreenTabs
                  : variantMinimizedTabs
              }
              animate={isOpen ? 'show' : 'hide'}
              initial="initial"
            >
              <InventoryTabs
                mode={mode}
                onChange={(index) => {
                  playClick()
                  setActiveTab(index)
                }}
                onClose={() => {
                  playClick()
                  toggle(false)
                }}
                tabIndex={activeTab}
                questsAmount={cardsQuest.length}
                skinsAmount={cardsSkins.length}
                itemsAmount={cardsitems.length}
              />
            </motion.div>

            {mode === 'fullscreen' ? (
              <motion.div
                variants={variantGrid}
                animate={isOpen ? 'show' : 'hide'}
                initial="initial"
                className={styles.pagerWrap}
              >
                <InventoryPager
                  value={activeTab}
                  animatedAxis="x"
                  onChange={(index) => {
                    playClick()
                    setActiveTab(index)
                  }}
                >
                  <InventoryGrid items={cardsQuest} type="quest" onClick={handleCardClick} />
                  <InventoryGrid items={cardsSkins} type="skins" onClick={handleCardClick} />
                  <InventoryGrid items={cardsitems} type="items" onClick={handleCardClick} />
                </InventoryPager>
              </motion.div>
            ) : null}

            {mode === 'minimized' ? (
              <motion.div
                variants={variantRow}
                animate={isOpen ? 'show' : 'hide'}
                initial="initial"
              >
                <InventoryPager
                  value={activeTab}
                  animatedAxis="y"
                  onChange={(index) => {
                    playClick()
                    setActiveTab(index)
                  }}
                >
                  <InventoryRow 
                    type="quest"
                    items={cardsQuest}
                    onClick={handleCardClick}
                    handleDrop={handleDrop}
                  />
                  <InventoryRow
                    type="skins"
                    items={cardsSkins}
                    onClick={handleCardClick}
                    handleDrop={handleDrop}
                  />
                  <InventoryRow
                    type="items"
                    items={cardsitems}
                    onClick={handleCardClick}
                    handleDrop={handleDrop}
                  />
                </InventoryPager>
              </motion.div>
            ) : null}
          </>
        ) : null}
      </motion.div>
    </div>
  )
})
